import type { ToolCallInfo } from "@/lib/api";
import { ArtifactDetail } from "./ArtifactDetail";

type Props = {
  tc: ToolCallInfo;
  onOpen: (artifactId: string) => void;
  inlineExpandable?: boolean;
  expanded?: boolean;
  onToggleInline?: () => void;
};

function extractStats(result: unknown): { id?: string; name?: string; path?: string; added?: number; removed?: number } {
  if (!result || typeof result !== "object") return {};
  const r = result as Record<string, unknown>;
  return {
    id: typeof r.id === "string" ? r.id : undefined,
    name: typeof r.name === "string" ? r.name : undefined,
    path: typeof r.path === "string" ? r.path : undefined,
    added: typeof r.added === "number" ? r.added : undefined,
    removed: typeof r.removed === "number" ? r.removed : undefined,
  };
}

export function ArtifactChip({ tc, onOpen, inlineExpandable, expanded, onToggleInline }: Props) {
  const stats = extractStats(tc.result);
  const argsRecord = tc.args as Record<string, unknown> | undefined;
  const argsId = typeof argsRecord?.id === "string" ? argsRecord.id : undefined;
  const id = stats.id ?? argsId;

  let label: string;
  if (tc.tool === "artifact_create") {
    label = `Created ${stats.name ?? "artifact"}`;
  } else if (tc.tool === "artifact_write" || tc.tool === "artifact_edit") {
    const path = stats.path ?? (typeof argsRecord?.path === "string" ? argsRecord.path : "file");
    const a = stats.added ?? 0;
    const r = stats.removed ?? 0;
    label = `Changed +${a} −${r} in ${path}`;
  } else if (tc.tool === "artifact_read") {
    const path = typeof argsRecord?.path === "string" ? argsRecord.path : null;
    label = path ? `Read ${path}` : "Read artifact";
  } else if (tc.tool === "artifact_delete") {
    label = `Deleted artifact`;
  } else {
    label = tc.tool;
  }

  const disabled = !id || tc.tool === "artifact_delete";
  const canExpand = inlineExpandable && !!id && tc.tool !== "artifact_delete";

  return (
    <div className={expanded ? "w-full" : "inline-flex"}>
      <div className="inline-flex max-w-full items-center gap-0.5 rounded-md border bg-muted/30 text-xs">
        <button
          type="button"
          onClick={() => id && onOpen(id)}
          disabled={disabled}
          className="flex items-center gap-1.5 px-2.5 py-1 text-foreground hover:bg-muted disabled:cursor-default disabled:opacity-60 rounded-l-md"
          title="Open in side panel"
        >
          <i className="fa-solid fa-file-code text-primary" />
          <span className="truncate">{label}</span>
        </button>
        {canExpand && onToggleInline && (
          <button
            type="button"
            onClick={onToggleInline}
            className="flex items-center justify-center border-l px-2 py-1 text-muted-foreground hover:bg-muted hover:text-foreground rounded-r-md"
            title={expanded ? "Collapse inline" : "Expand inline"}
          >
            <i className={`fa-solid fa-chevron-${expanded ? "up" : "down"} text-[10px]`} />
          </button>
        )}
      </div>
      {expanded && id && (
        <div className="mt-2 h-[70vh] overflow-hidden rounded-md border bg-background shadow-sm">
          <ArtifactDetail id={id} />
        </div>
      )}
    </div>
  );
}
